import CloseIcon from '@mui/icons-material/Close';
import {
    alpha,
    Box,
    Button,
    CircularProgress,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import Alert from '/@/components/bayer/alert';
import ModalV2 from '/@/components/bayer/modal-v2';
import useUnitProvider from '/@/core/context/unit';
import { useOwnUserNode } from '/@/hooks/nodes';
import { useCreateOrUpdateCropMonitoringResult } from '/@/hooks/reports';
import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import { socialLogin } from '/@/store/session/actions';
import type { CropMonitoring, CropType } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { reverse } from '/@/utils/router';
import { getCrops } from '/@/views/dashboard/hooks/crops';
import { useModalReportForm } from '../../hooks/modal-report-form';
import { MAX_ENVIRONMENT, MIN_ENVIRONMENT } from '../creation-dialog';
import EnvironmentInput from '../creation-dialog/components/EnvironmentInput';

const useAlertAndTitle = (
    type: 'fieldview' | 'manual',
    plantingDateOptions: any,
    harvestDateOptions: any,
    fvError: any
) =>
    useMemo<{ alert?: string; title?: string }>(() => {
        let title = _('Completá los datos de tu cosecha');
        if (type === 'manual') return { title };

        // No result message
        const hasNoPlantingData = !plantingDateOptions.length;
        const hasNoHarvestData = !harvestDateOptions.length;
        if (fvError && type === 'fieldview') {
            return {
                alert: '',
                title,
            };
        }

        if (hasNoPlantingData && hasNoHarvestData) {
            return {
                alert: _('No encontramos datos de tu campaña en Fieldview. Cargalos para continuar.'),
                title,
            };
        }
        if (hasNoHarvestData) {
            return {
                alert: _('Completá los datos de tu cosecha'),
                title,
            };
        }

        title = _('Revisá y confirmá los datos de tu cosecha');
        if (hasNoPlantingData) {
            return { title };
        }

        return { title };
    }, [plantingDateOptions, harvestDateOptions, fvError, type]);

const CreateReportModal = ({
    field,
    cropMonitoring,
    isOpen,
    setIsOpen,
    type = 'manual',
    showErrorModal,
    setShowErrorModal,
}: {
    field: Field;
    cropMonitoring: CropMonitoring;
    isOpen: boolean;
    setIsOpen: (boolean: boolean) => void;
    type?: 'fieldview' | 'manual';
    showErrorModal: boolean;
    setShowErrorModal: (boolean: boolean) => void;
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const units = useUnitProvider();
    const { plantingDateOptions, harvestDateOptions, form, fvError, createReports } = useModalReportForm({
        field,
        cropMonitoring,
        type,
    });
    const createOrUpdateCropMonitoringResult = useCreateOrUpdateCropMonitoringResult({
        cropMonitoringId: cropMonitoring?.id,
        fieldId: field.id,
    });

    const plantingDate = form.watch('plantingDate');
    const harvestDate = form.watch('harvestDate');
    const campaign = useMemo(() => {
        if (plantingDate && harvestDate) return `${plantingDate?.year()} - ${harvestDate?.year()}`;
        if (plantingDate) return plantingDate?.year();
        if (harvestDate) return harvestDate?.year();
        return '';
    }, [plantingDate, harvestDate]);

    const { title, alert } = useAlertAndTitle(type, plantingDateOptions, harvestDateOptions, fvError);

    // Handle showing error modal when there's an fvError
    useEffect(() => {
        if (fvError && type === 'fieldview') {
            setShowErrorModal(true);
        }
    }, [fvError, type, setShowErrorModal]);

    const renderPlantingDateSelector = Boolean(plantingDateOptions.length && form.watch('plantingResultId'));
    const renderHarvestDateSelector = Boolean(harvestDateOptions.length && form.watch('harvestResultId'));

    const isBayerUserType = useIsValidatedBayerUserType();
    const ownUserQuery = useOwnUserNode();
    const showLoginButton = !isBayerUserType || field.userNode === ownUserQuery.data?.id; // If the user is not RV or the user is the owner of the field, show the login button
    const crops = getCrops();
    return (
        <Stack>
            <ModalV2
                open={showErrorModal}
                onClose={() => {
                    setShowErrorModal(false);
                    setIsOpen(true);
                }}
                header={
                    <Stack direction='row' justifyContent='end'>
                        <IconButton>
                            <CloseIcon
                                onClick={() => {
                                    setShowErrorModal(false);
                                    setIsOpen(true);
                                }}
                            />
                        </IconButton>
                    </Stack>
                }
                actions={
                    <>
                        <Button
                            variant='text'
                            color='secondary'
                            onClick={() => {
                                setIsOpen(true);
                                setShowErrorModal(false);
                            }}
                        >
                            {_('Cargar manualmente')}
                        </Button>
                        {showLoginButton && (
                            <Button
                                type='submit'
                                variant='contained'
                                color='secondary'
                                onClick={() => {
                                    dispatch(
                                        socialLogin({
                                            provider: 'fieldview',
                                            history: navigate,
                                            redirectTo: location.pathname,
                                            source: 'field-reports',
                                            onSuccess: () => {
                                                createReports();
                                            },
                                        })
                                    );
                                }}
                            >
                                {_('Iniciar sesión')}
                            </Button>
                        )}
                    </>
                }
                actionsProps={{ sx: { justifyContent: 'end' } }}
                maxWidth={'sm'}
            >
                <Stack spacing={2} direction='column' alignItems='center'>
                    <Typography color='primary' variant='h6'>
                        {_('Hubo un problema al importar los datos de tu cosecha')}
                    </Typography>
                    <Typography color='primary' variant='subtitle1'>
                        {_(
                            'Volvé a intentarlo. Si el problema persiste, probá de nuevo más tarde o cargá los datos manualmente.'
                        )}
                    </Typography>
                </Stack>
            </ModalV2>
            <Modal
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgb(255 255 255)',
                    },
                }}
                open={createOrUpdateCropMonitoringResult.isMutating}
            >
                <Stack spacing={4} alignItems='center' justifyContent='center' height='100%'>
                    <Typography color='primary' variant='h5'>
                        {_('Generando reporte...')}
                    </Typography>
                    <Box
                        sx={{
                            color: 'primary.onSurface',
                        }}
                    >
                        <CircularProgress color='inherit' size={75} />
                    </Box>
                    <Typography color='primary.mui.inactive' variant='body2'>
                        {_('Vas a poder ver cómo fue tu campaña y definir los próximos pasos con más datos.')}
                    </Typography>
                </Stack>
            </Modal>

            <ModalV2
                open={isOpen && !showErrorModal}
                onClose={() => {
                    setIsOpen(false);
                }}
                actions={
                    <>
                        <Button
                            variant='text'
                            color='secondary'
                            onClick={() => {
                                setIsOpen(false);
                                form.reset();
                            }}
                            disabled={createOrUpdateCropMonitoringResult.isMutating}
                        >
                            {_('Cancelar')}
                        </Button>
                        <Button
                            type='submit'
                            variant='contained'
                            color='secondary'
                            disabled={createOrUpdateCropMonitoringResult.isMutating}
                            startIcon={
                                createOrUpdateCropMonitoringResult.isMutating ? (
                                    <CircularProgress size={20} />
                                ) : undefined
                            }
                        >
                            {_('Generar reporte')}
                        </Button>
                    </>
                }
                title={title}
                actionsProps={{ sx: { justifyContent: 'end' } }}
                maxWidth={'sm'}
                containerProps={{
                    component: 'form',
                    onSubmit: form.handleSubmit(
                        async (data) => {
                            await createOrUpdateCropMonitoringResult.trigger({
                                plantingReport: data.plantingRequestId || undefined,
                                harvestReport: data.harvestRequestId || undefined,

                                plantingDate: data.plantingDate?.format('YYYY-MM-DD'),

                                harvestDate: data.harvestDate?.format('YYYY-MM-DD'),
                                averageYield: data.yield ?? undefined,
                                humidityAtHarvest: data.humidity ? Number(data.humidity) : undefined,
                            });
                            setIsOpen(false);
                            navigate(
                                reverse('cropMonitoring.CornCropMonitoringDetail.report', {
                                    params: { cropMonitoringId: cropMonitoring.id },
                                })
                            );
                        },
                        (data) => {}
                    ),
                }}
            >
                <Stack spacing={1.5} textAlign='left'>
                    <Typography variant='body2' color='primary.mui.inactive'>
                        {_('Vamos a generar un reporte con los resultados de tu lote para esta campaña.')}
                    </Typography>

                    <Stack
                        spacing={0.5}
                        p={2}
                        sx={{
                            background: (t) => alpha(t.palette.primary.main, 0.04),
                        }}
                    >
                        <Stack direction='row' spacing={0.5}>
                            <Typography color='primary' fontWeight='bold'>
                                {_('Cultivo:')}
                            </Typography>
                            <Typography color='primary.mui.inactive'>
                                {crops[cropMonitoring?.crop as keyof typeof crops]?.name}
                            </Typography>
                        </Stack>

                        <Stack direction='row' spacing={0.5}>
                            <Typography color='primary' fontWeight='bold'>
                                {_('Híbrido:')}
                            </Typography>
                            <Typography color='primary.mui.inactive'>{cropMonitoring?.productName}</Typography>
                        </Stack>

                        <Stack direction='row' spacing={0.5}>
                            <Typography color='primary' fontWeight='bold'>
                                {_('Lote:')}
                            </Typography>
                            <Typography color='primary.mui.inactive'>{field?.name}</Typography>
                        </Stack>

                        <Stack direction='row' spacing={0.5}>
                            <Typography color='primary' fontWeight='bold'>
                                {_('Campaña:')}
                            </Typography>
                            <Typography color='primary.mui.inactive'>{campaign}</Typography>
                        </Stack>
                    </Stack>
                    {alert && <Alert severity='danger'>{alert}</Alert>}
                    <Stack>
                        <Typography color='primary' variant='subtitle1' fontWeight='bold'>
                            {_('Fecha de cosecha')}
                        </Typography>
                        {renderHarvestDateSelector && (
                            <Controller
                                name='harvestResultId'
                                control={form.control}
                                rules={{
                                    required: _('Campo requerido'),
                                }}
                                render={({ field, fieldState }) => (
                                    <FormControl>
                                        <InputLabel id='harvest-date'>{_('Seleccione una fecha')}</InputLabel>
                                        <Select
                                            {...field}
                                            labelId='harvest-date'
                                            label={_('Seleccione una fecha')}
                                            onChange={(e) => {
                                                const selected = harvestDateOptions.find(
                                                    (opt) => opt.value === e.target.value
                                                );
                                                if (selected) {
                                                    form.setValue('harvestDate', selected.date);
                                                    form.setValue('harvestRequestId', selected.requestId);
                                                    field.onChange(e);
                                                }
                                            }}
                                            error={Boolean(fieldState.error?.message)}
                                            defaultValue={null}
                                            sx={{
                                                '& .MuiSelect-select': {
                                                    whiteSpace: 'unset !important',
                                                },
                                            }}
                                        >
                                            {harvestDateOptions.map((pd) => (
                                                <MenuItem key={pd.value} value={pd.value}>
                                                    {pd.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <Typography variant='caption' color='error' sx={{ ml: '14px', mt: '3px' }}>
                                            {fieldState.error?.message}
                                        </Typography>
                                    </FormControl>
                                )}
                            />
                        )}

                        {!renderHarvestDateSelector && (
                            <Controller
                                name='harvestDate'
                                control={form.control}
                                rules={{
                                    required: _('Campo requerido'),
                                    validate: (value, formValues) => {
                                        if (value?.isBefore(formValues.plantingDate))
                                            return _('La fecha de cosecha debe ser posterior a la fecha de siembra.');
                                        return true;
                                    },
                                }}
                                render={({ field, fieldState }) => (
                                    <FormControl>
                                        <MuiDatePicker
                                            {...field}
                                            label={_('Seleccione una fecha')}
                                            onChange={(e) => {
                                                field.onChange(e);
                                                form.setValue('harvestResultId', null);
                                                form.setValue('harvestRequestId', null);
                                            }}
                                            minDate={
                                                plantingDateOptions.length > 0 ? form.watch('plantingDate') : undefined
                                            }
                                            maxDate={dayjs()}
                                            slotProps={{
                                                textField: {
                                                    error: Boolean(fieldState.error?.message),
                                                },
                                            }}
                                            defaultValue={null}
                                        />
                                        <Typography variant='caption' color='error' sx={{ ml: '14px', mt: '3px' }}>
                                            {fieldState.error?.message}
                                        </Typography>
                                    </FormControl>
                                )}
                            />
                        )}
                    </Stack>

                    <Stack>
                        <Typography color='primary' variant='subtitle1' fontWeight='bold'>
                            {_('Fecha de siembra')}
                        </Typography>
                        {renderPlantingDateSelector && (
                            <Controller
                                name='plantingResultId'
                                control={form.control}
                                rules={{
                                    required: _('Campo requerido'),
                                }}
                                render={({ field, fieldState }) => (
                                    <FormControl>
                                        <InputLabel id='planting-date'>{_('Seleccione una fecha')}</InputLabel>
                                        <Select
                                            {...field}
                                            labelId='planting-date'
                                            label={_('Seleccione una fecha')}
                                            onChange={(e) => {
                                                const selected = plantingDateOptions.find(
                                                    (opt) => opt.value === e.target.value
                                                );
                                                if (selected) {
                                                    form.setValue('plantingDate', selected.date);
                                                    form.setValue('plantingRequestId', selected.requestId);
                                                    field.onChange(e);
                                                }
                                            }}
                                            error={Boolean(fieldState.error?.message)}
                                            defaultValue={null}
                                            sx={{
                                                '& .MuiSelect-select': {
                                                    whiteSpace: 'unset !important',
                                                },
                                            }}
                                        >
                                            {plantingDateOptions.map((pd) => (
                                                <MenuItem
                                                    key={pd.value}
                                                    value={pd.value}
                                                    disabled={harvestDate && pd.date.isAfter(harvestDate)}
                                                >
                                                    {pd.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <Typography variant='caption' color='error' sx={{ ml: '14px', mt: '3px' }}>
                                            {fieldState.error?.message}
                                        </Typography>
                                    </FormControl>
                                )}
                            />
                        )}
                        {!renderPlantingDateSelector && (
                            <Controller
                                name='plantingDate'
                                control={form.control}
                                rules={{
                                    required: _('Campo requerido'),
                                    validate: (value, formValues) => {
                                        if (value?.isAfter(formValues.harvestDate))
                                            return _('La fecha de siembra debe ser anterior a la fecha de cosecha.');
                                        return true;
                                    },
                                }}
                                render={({ field, fieldState }) => (
                                    <FormControl>
                                        <MuiDatePicker
                                            {...field}
                                            label={_('Seleccione una fecha')}
                                            onChange={(e) => {
                                                field.onChange(e);
                                                form.setValue('plantingResultId', null);
                                                form.setValue('plantingRequestId', null);
                                            }}
                                            maxDate={
                                                harvestDateOptions.length > 0 ? form.watch('harvestDate') : dayjs()
                                            }
                                            slotProps={{
                                                textField: {
                                                    error: Boolean(fieldState.error?.message),
                                                },
                                            }}
                                            defaultValue={null}
                                        />
                                        <Typography variant='caption' color='error' sx={{ ml: '14px', mt: '3px' }}>
                                            {fieldState.error?.message}
                                        </Typography>
                                    </FormControl>
                                )}
                            />
                        )}
                    </Stack>

                    <Stack>
                        <Controller
                            name='yield'
                            control={form.control}
                            rules={{
                                required: _('Campo requerido'),
                            }}
                            render={({ field, fieldState }) => (
                                <EnvironmentInput
                                    valueType={_('rendimiento')}
                                    {...field}
                                    crop={cropMonitoring?.crop as CropType}
                                    value={field.value || undefined}
                                    onChange={field.onChange}
                                    units={units}
                                    error={fieldState.error?.message}
                                    minEnvironment={MIN_ENVIRONMENT}
                                    maxEnvironment={MAX_ENVIRONMENT}
                                />
                            )}
                        />
                    </Stack>

                    <Stack>
                        <Typography color='primary' variant='subtitle1' fontWeight='bold'>
                            {`${_('Humedad al cosechar')}`}
                        </Typography>
                        <Controller
                            name='humidity'
                            control={form.control}
                            rules={{
                                required: _('Campo requerido'),
                            }}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    type='number'
                                    InputProps={{
                                        endAdornment: <InputAdornment position='end'>%</InputAdornment>,
                                    }}
                                    error={fieldState.error?.message !== undefined}
                                    disabled={renderHarvestDateSelector}
                                />
                            )}
                        />
                    </Stack>
                </Stack>
            </ModalV2>
        </Stack>
    );
};

export default CreateReportModal;
